import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { navegarComCtrl } from "../../utils/solicitacoes/navegacao";
import Chart from "react-apexcharts";
import { CustomTable } from "../../componentes/table/CustomTable";
import { CustomDropdown } from "../../componentes/dropdown/CustomDropdown";
import StatusBadge from "../../components/solicitacoes/Shared/StatusBadge";
import { ChartCard } from "../../componentes/chart/CardChart";
import TabelaEntriesGrafico from "../../components/solicitacoes/Dashboard/TabelaEntriesGrafico";
import DashboardProcessoLayout, { Campo, INPUT_CLASS } from "../../components/solicitacoes/Dashboard/DashboardProcessoLayout";
import { useDashboardProcesso } from "../../hooks/solicitacoes/useDashboardProcesso";
import MultaService from "../../services/resource/multaService";
import UsuarioService from "../../services/resource/usuarioService";
import FilialService from "../../services/resource/filialService";
import { STATUS_CONFIG } from "../../utils/solicitacoes/statusConfig";
import { processoInfo } from "../../utils/solicitacoes/processoRegistry";
import { formatarMoeda } from "../../utils/solicitacoes/formatters";
import { donutOptions, barOptions, CORES_GRAFICO } from "../../utils/solicitacoes/chartOptions";

const service = new MultaService();
const usuarioService = new UsuarioService();
const filialService = new FilialService();

const STATUS_OPTIONS = Object.entries(STATUS_CONFIG).map(([value, info]) => ({ value, label: info.label }));
const ETAPA_OPTIONS = (processoInfo("PROCESSO_MULTAS")?.etapas || []).map(e => ({ value: e.codigo, label: e.label }));

const FILTROS_INICIAIS_EXTRA = {
    unidadeId: null, veiculoPlaca: "", condutorNome: "", natureza: "",
    status: null, etapaAtual: null, solicitanteId: null, responsavelId: null,
};

const COLUNAS = [
    { key: "numero", label: "Número", width: 120 },
    { key: "dataSolicitacao", label: "Data do cadastro", format: "date" },
    { key: "solicitanteNome", label: "Solicitante" },
    { key: "unidadeNome", label: "Unidade" },
    { key: "veiculoPlaca", label: "Veículo" },
    { key: "renainf", label: "Renainf" },
    { key: "dataInfracao", label: "Data da Infração", format: "date" },
    { key: "local", label: "Local" },
    { key: "natureza", label: "Natureza" },
    { key: "valorMulta", label: "Valor da Multa", format: "currency" },
    { key: "valorBoleto", label: "Valor do Boleto", format: "currency" },
    { key: "dataVencimento", label: "Vencimento", format: "date" },
    { key: "condutorNome", label: "Condutor" },
    { key: "valorDescontado", label: "Valor Descontado", format: "currency" },
    { key: "parcelamento", label: "Parcelamento" },
    { key: "status", label: "Status", render: v => <StatusBadge status={v} />, center: true },
    { key: "etapaAtual", label: "Etapa Atual" },
    { key: "responsavelAtualNome", label: "Responsável" },
];

export default function DashboardMultas() {
    const navigate = useNavigate();
    const [usuarios, setUsuarios] = useState([]);
    const [filiais, setFiliais] = useState([]);

    const { filtros, setFiltros, limparFiltros, pesquisar, linhas, dashboard, loading } = useDashboardProcesso({
        service, chaveFiltros: "solicitacoes.dashboard.multas.filtros", filtrosIniciaisExtra: FILTROS_INICIAIS_EXTRA,
    });

    useEffect(() => {
        document.title = "MyDash | Dashboard de Processo de Multas";
    }, []);

    useEffect(() => {
        usuarioService.listarUsuarios().then(res => setUsuarios(res.data)).catch(() => setUsuarios([]));
        filialService.listarAtivas().then(res => setFiliais(res.data)).catch(() => setFiliais([]));
    }, []);

    function setFiltro(campo, valor) {
        setFiltros(prev => ({ ...prev, [campo]: valor }));
    }

    const porStatusEntries = Object.entries(dashboard?.porStatus || {}).map(([k, v]) => [STATUS_CONFIG[k]?.label || k, v]);
    const porEtapaEntries = Object.entries(dashboard?.porEtapa || {});
    const evolucaoEntries = Object.entries(dashboard?.evolucaoPeriodo || {});

    const [viewStatus, setViewStatus] = useState("chart");
    const [viewEtapa, setViewEtapa] = useState("chart");
    const [viewEvolucao, setViewEvolucao] = useState("chart");

    return (
        <DashboardProcessoLayout
            titulo="Dashboard de Processo de Multas"
            onNovaSolicitacao={() => navigate("/solicitacoes/nova")}
            loading={loading}
            filtros={filtros}
            onFiltroChange={setFiltro}
            onPesquisar={pesquisar}
            onLimparFiltros={limparFiltros}
            kpis={[
                { label: "Total de multas", value: dashboard?.total ?? 0, icon: "fa fa-gavel", iconClassName: "bg-primary/10 text-primary", colspan: 'lg:col-span-2' },
                { label: "Pendentes", value: dashboard?.pendentes ?? 0, icon: "fa fa-clock", iconClassName: "bg-amber-100 text-amber-700" , colspan: 'lg:col-span-2'},
                { label: "Finalizadas", value: dashboard?.finalizadas ?? 0, icon: "fa fa-check-circle", iconClassName: "bg-success/10 text-success" , colspan: 'lg:col-span-2'},
                { label: "Valor Total das Multas", value: formatarMoeda(dashboard?.valorTotalMultas ?? 0), icon: "fa fa-sack-dollar", iconClassName: "bg-blue-100 text-blue-700", colspan: 'lg:col-span-2' },
            ]}
            graficos={(
                <>
                    <div className="lg:col-span-6">
                        <ChartCard title="Multas por status" view={viewStatus} onViewChange={setViewStatus} accent={CORES_GRAFICO[0]}
                            tableContent={<TabelaEntriesGrafico entries={porStatusEntries} colunaValor="Quantidade" />}>
                            {porStatusEntries.length === 0 ? (
                                <p className="text-sm text-muted-foreground flex items-center justify-center h-64">Sem dados para o filtro atual.</p>
                            ) : (
                                <Chart type="donut" height="100%" series={porStatusEntries.map(([, v]) => v)}
                                    options={donutOptions(porStatusEntries.map(([k]) => k))} />
                            )}
                        </ChartCard>
                    </div>
                    <div className="lg:col-span-6">
                        <ChartCard title="Multas por etapa" view={viewEtapa} onViewChange={setViewEtapa} accent={CORES_GRAFICO[1]}
                            tableContent={<TabelaEntriesGrafico entries={porEtapaEntries} colunaValor="Quantidade" />}>
                            {porEtapaEntries.length === 0 ? (
                                <p className="text-sm text-muted-foreground flex items-center justify-center h-64">Sem dados para o filtro atual.</p>
                            ) : (
                                <Chart type="donut" height="100%" series={porEtapaEntries.map(([, v]) => v)}
                                    options={donutOptions(porEtapaEntries.map(([k]) => k))} />
                            )}
                        </ChartCard>
                    </div>
                    <div className="lg:col-span-12">
                        <ChartCard title="Evolução no período" view={viewEvolucao} onViewChange={setViewEvolucao} accent={CORES_GRAFICO[2]}
                            tableContent={<TabelaEntriesGrafico entries={evolucaoEntries} colunaValor="Quantidade" />}>
                            {evolucaoEntries.length === 0 ? (
                                <p className="text-sm text-muted-foreground flex items-center justify-center h-64">Sem dados para o filtro atual.</p>
                            ) : (
                                <Chart type="bar" height="100%" series={[{ name: "Multas", data: evolucaoEntries.map(([, v]) => v) }]}
                                    options={barOptions(evolucaoEntries.map(([k]) => k))} />
                            )}
                        </ChartCard>
                    </div>
                </>
            )}
            filtrosExtras={(
                <>
                    <Campo label="Unidade">
                        <CustomDropdown value={filtros.unidadeId} onChange={e => setFiltro("unidadeId", e.value)} options={filiais}
                            optionValue="id" optionLabel="nome" placeholder="Todas as unidades" showClear filter />
                    </Campo>
                    <Campo label="Veículo (placa)">
                        <input type="text" value={filtros.veiculoPlaca} onChange={e => setFiltro("veiculoPlaca", e.target.value)} className={INPUT_CLASS} />
                    </Campo>
                    <Campo label="Condutor">
                        <input type="text" value={filtros.condutorNome} onChange={e => setFiltro("condutorNome", e.target.value)} className={INPUT_CLASS} />
                    </Campo>
                    <Campo label="Natureza">
                        <input type="text" value={filtros.natureza} onChange={e => setFiltro("natureza", e.target.value)} className={INPUT_CLASS} />
                    </Campo>
                    <Campo label="Status">
                        <CustomDropdown value={filtros.status} onChange={e => setFiltro("status", e.value)} options={STATUS_OPTIONS}
                            placeholder="Todos os status" showClear />
                    </Campo>
                    <Campo label="Etapa atual">
                        <CustomDropdown value={filtros.etapaAtual} onChange={e => setFiltro("etapaAtual", e.value)} options={ETAPA_OPTIONS}
                            placeholder="Todas as etapas" showClear />
                    </Campo>
                    <Campo label="Solicitante">
                        <CustomDropdown value={filtros.solicitanteId} onChange={e => setFiltro("solicitanteId", e.value)} options={usuarios}
                            optionValue="id" optionLabel="nome" placeholder="Todos os solicitantes" showClear filter />
                    </Campo>
                    <Campo label="Responsável">
                        <CustomDropdown value={filtros.responsavelId} onChange={e => setFiltro("responsavelId", e.value)} options={usuarios}
                            optionValue="id" optionLabel="nome" placeholder="Todos os responsáveis" showClear filter />
                    </Campo>
                </>
            )}
        >
            <CustomTable
                data={linhas}
                columns={COLUNAS}
                loading={loading}
                pagination
                pageSize={20}
                exportable
                showHeader={false}
                className="text-[11px]"
                exportFilename="dashboard_multas"
                footer={() => null}
                onRowSelect={(row, e) => navegarComCtrl(navigate, `/solicitacoes/${row.solicitacaoId}`, e)}
            />
        </DashboardProcessoLayout>
    );
}
